/**
 * useKeyboardVisibility Hook
 *
 * Detects virtual keyboard visibility on mobile web browsers.
 * Uses visualViewport API + focus detection for cross-browser support.
 *
 * Chrome Android and Safari iOS have different viewport behaviors:
 * - Safari: Only resizes Visual Viewport (visualViewport API works)
 * - Chrome: May not fire visualViewport events reliably
 *
 * This hook uses BOTH approaches for maximum compatibility:
 * 1. visualViewport.resize events (works on Safari)
 * 2. focusin/focusout on input elements (fallback for Chrome)
 */
import { useState, useEffect, useRef } from "react";
import { Platform } from "react-native";

interface UseKeyboardVisibilityOptions {
  debounceDelay?: number;
  detectionRatio?: number;
}

interface KeyboardVisibilityState {
  isKeyboardVisible: boolean;
  keyboardHeight: number;
}

export function useKeyboardVisibility(
  options: UseKeyboardVisibilityOptions = {}
): KeyboardVisibilityState {
  const { debounceDelay = 100, detectionRatio = 0.15 } = options;

  const [viewportDetected, setViewportDetected] = useState(false);
  const [focusDetected, setFocusDetected] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const baseHeightRef = useRef<number | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (Platform.OS !== "web") return;

    // Initialize base height
    baseHeightRef.current = window.visualViewport?.height || window.innerHeight;

    function handleResize() {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      debounceTimerRef.current = setTimeout(() => {
        const isMobile = window.innerWidth <= 768;
        if (!isMobile) {
          setViewportDetected(false);
          setKeyboardHeight(0);
          return;
        }

        const currentHeight = window.visualViewport?.height || window.innerHeight;
        const baseHeight = baseHeightRef.current || currentHeight;
        const heightDifference = baseHeight - currentHeight;
        const isKeyboard = heightDifference > baseHeight * detectionRatio;

        if (currentHeight > baseHeight) {
          baseHeightRef.current = currentHeight;
        }

        setViewportDetected(isKeyboard);
        setKeyboardHeight(isKeyboard ? heightDifference : 0);
      }, debounceDelay);
    }

    function handleFocusIn(event: FocusEvent) {
      const target = event.target as HTMLElement;
      const isInput = target?.tagName === "INPUT" || 
                      target?.tagName === "TEXTAREA" || 
                      target?.isContentEditable;
      if (isInput) {
        setFocusDetected(true);
        // Trigger resize check after Chrome keyboard animation
        setTimeout(handleResize, 350);
      }
    }

    function handleFocusOut(event: FocusEvent) {
      // Small delay to handle focus moving between inputs
      setTimeout(() => {
        const active = document.activeElement;
        const isStillInInput = active?.tagName === "INPUT" || 
                               active?.tagName === "TEXTAREA" || 
                               (active as HTMLElement)?.isContentEditable;
        if (!isStillInInput) {
          setFocusDetected(false);
        }
      }, 100);
    }

    window.visualViewport?.addEventListener("resize", handleResize);
    window.addEventListener("resize", handleResize);
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    return () => {
      window.visualViewport?.removeEventListener("resize", handleResize);
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [debounceDelay, detectionRatio]);

  // Combine both detection methods - either triggers keyboard visible
  return {
    isKeyboardVisible: viewportDetected || focusDetected,
    keyboardHeight,
  };
}

export default useKeyboardVisibility;
